import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppState } from '../../contexts/AppStateContext';
import FileUpload from '../common/FileUpload';
import BatchFileUpload from '../common/BatchFileUpload';
import ToolHeader from '../common/ToolHeader';
import ProgressBar from '../common/ProgressBar';
import ImageComparisonSlider from '../common/ImageComparisonSlider';
import { loadImage, smartCompressImage, calculateCompressionStats } from '../../utils/imageUtils';
import { formatFileSize, downloadFile, estimateProcessingTime, formatRemainingTime, calculateProcessingSpeed, cleanupBlobUrls, getMemoryInfo } from '../../utils/fileUtils';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiDownload, FiLoader, FiInfo, FiLayers } = FiIcons;

const ImageCompressTool = () => {
  const { t } = useLanguage();
  const { state, dispatch } = useAppState();
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  // 處理時間追蹤
  const [startTime, setStartTime] = useState(null);
  const [remainingTime, setRemainingTime] = useState(null);
  const [processingSpeed, setProcessingSpeed] = useState(null);

  // 🆕 批量處理狀態
  const [batchMode, setBatchMode] = useState(false);
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchResults, setBatchResults] = useState([]);
  const [currentProcessingIndex, setCurrentProcessingIndex] = useState(0);

  // 🐛 修復:單個文件模式下才加載預覽
  useEffect(() => {
    if (state.selectedFile && state.selectedFile.type.startsWith('image/') && !batchMode) {
      loadImagePreview();
    }
  }, [state.selectedFile, batchMode]);

  // 清理效果
  useEffect(() => {
    return () => {
      if (state.imagePreview) {
        cleanupBlobUrls(state.imagePreview);
      }
      if (state.compressedImageUrl) {
        cleanupBlobUrls(state.compressedImageUrl);
      }
      // 清理批量結果
      batchResults.forEach(result => {
        if (result.compressedUrl) {
          cleanupBlobUrls(result.compressedUrl);
        }
      });
    };
  }, []);

  const loadImagePreview = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      setProgress(20);
      setProgressMessage(t('loadingPdf'));

      const img = await loadImage(state.selectedFile);
      const url = URL.createObjectURL(state.selectedFile);

      setProgress(100);
      setProgressMessage(t('downloadComplete'));

      dispatch({
        type: 'SET_IMAGE_PREVIEW',
        payload: {
          url,
          width: img.naturalWidth,
          height: img.naturalHeight
        }
      });

      dispatch({ type: 'SET_LOADING', payload: false });
      setProgress(0);
      setProgressMessage('');
    } catch (error) {
      console.error('圖片加載錯誤:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      if (window.showToast) {
        window.showToast(error.message, 'error');
      }
      setProgress(0);
      setProgressMessage('');
    }
  };

  const handleFileUpload = (file) => {
    if (!file.type.startsWith('image/')) {
      if (window.showToast) {
        window.showToast(t('invalidFileType'), 'error');
      }
      return;
    }

    setBatchMode(false);
    setBatchFiles([]);
    setBatchResults([]);
    dispatch({ type: 'SET_SELECTED_FILE', payload: file });
  };

  // 🐛 修復:批量文件上傳處理
  const handleBatchFilesUpload = (files) => {
    if (files.length === 0) return;

    setBatchMode(true);
    setBatchFiles(files);
    setBatchResults([]);
    setCurrentProcessingIndex(0);

    // 不設置 selectedFile,因為批量模式不需要
    dispatch({
      type: 'UPDATE_SETTINGS',
      payload: {
        showSettings: true,
        showResults: false
      }
    });
  };

  const handleCompress = async () => {
    if (!state.selectedFile && !batchMode) {
      if (window.showToast) {
        window.showToast(t('pleaseUploadFile'), 'warning');
      }
      return;
    }

    if (batchMode && batchFiles.length > 0) {
      await handleBatchCompress();
    } else {
      await handleSingleCompress();
    }
  };

  // 單個文件壓縮
  const handleSingleCompress = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const start = Date.now();
      setStartTime(start);
      const estimated = estimateProcessingTime(state.selectedFile.size, 'compress');

      setProgress(10);
      setProgressMessage(t('compressingPleaseWait'));
      setRemainingTime(formatRemainingTime(estimated));

      const img = await loadImage(state.selectedFile);

      setProgress(30);
      setProgressMessage(t('processing'));

      const elapsed = (Date.now() - start) / 1000;
      const remaining = Math.max(0, estimated - elapsed);
      setRemainingTime(formatRemainingTime(remaining));

      const blob = await smartCompressImage(
        img,
        state.selectedFile.size,
        state.imageFormat
      );

      setProgress(90);
      setProgressMessage(t('downloadComplete'));

      const totalElapsed = (Date.now() - start) / 1000;
      const speed = calculateProcessingSpeed(state.selectedFile.size, totalElapsed);
      setProcessingSpeed(speed);

      const url = URL.createObjectURL(blob);

      dispatch({
        type: 'SET_COMPRESSED_IMAGE',
        payload: {
          blob,
          url,
          size: blob.size
        }
      });

      setProgress(100);
      setProgressMessage(t('downloadComplete'));
      setRemainingTime(t('downloadComplete'));

      const memInfo = getMemoryInfo();
      if (memInfo) {
        console.log('記憶體使用:', memInfo);
      }

      if (window.showToast) {
        window.showToast(t('compressionSuccess'), 'success');
      }
    } catch (error) {
      console.error('壓縮錯誤:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      if (window.showToast) {
        window.showToast(error.message, 'error');
      }
    } finally {
      setTimeout(() => {
        setProgress(0);
        setProgressMessage('');
        setRemainingTime(null);
        setProcessingSpeed(null);
        setStartTime(null);
      }, 500);
    }
  };

  // 🆕 批量壓縮
  const handleBatchCompress = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const results = [];
      const totalFiles = batchFiles.length;
      const start = Date.now();

      for (let i = 0; i < totalFiles; i++) {
        const file = batchFiles[i];
        setCurrentProcessingIndex(i);

        const fileProgress = ((i / totalFiles) * 100);
        setProgress(fileProgress);
        setProgressMessage(`${t('processing')} ${i + 1}/${totalFiles}: ${file.name}`);

        try {
          const img = await loadImage(file);
          const blob = await smartCompressImage(
            img,
            file.size,
            state.imageFormat
          );

          const url = URL.createObjectURL(blob);
          const stats = calculateCompressionStats(file.size, blob.size);

          results.push({
            originalFile: file,
            compressedBlob: blob,
            compressedUrl: url,
            stats,
            success: true
          });
        } catch (error) {
          console.error(`壓縮 ${file.name} 失敗:`, error);
          results.push({
            originalFile: file,
            error: error.message,
            success: false
          });
        }
      }

      setProgress(100);
      setProgressMessage(t('batchCompressionComplete'));

      const totalElapsed = (Date.now() - start) / 1000;
      const totalSize = batchFiles.reduce((sum, file) => sum + file.size, 0);
      const speed = calculateProcessingSpeed(totalSize, totalElapsed);
      setProcessingSpeed(speed);

      setBatchResults(results);
      dispatch({
        type: 'UPDATE_SETTINGS',
        payload: { showResults: true }
      });

      const successCount = results.filter(r => r.success).length;
      if (window.showToast) {
        window.showToast(`${t('successfullyProcessed')} ${successCount}/${totalFiles} ${t('files')}`, 'success');
      }
    } catch (error) {
      console.error('批量壓縮錯誤:', error);
      if (window.showToast) {
        window.showToast(error.message, 'error');
      }
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
      setTimeout(() => {
        setProgress(0);
        setProgressMessage('');
        setProcessingSpeed(null);
      }, 1000);
    }
  };

  const handleDownload = () => {
    if (state.compressedImageBlob) {
      const extension = state.imageFormat === 'image/jpeg' ? 'jpg' : state.imageFormat === 'image/png' ? 'png' : 'webp';
      const originalName = state.selectedFile.name.split('.')[0];
      downloadFile(state.compressedImageBlob, `${originalName}-compressed.${extension}`);
    }
  };

  // 🆕 批量下載
  const handleBatchDownload = () => {
    const extension = state.imageFormat === 'image/jpeg' ? 'jpg' : state.imageFormat === 'image/png' ? 'png' : 'webp';

    batchResults.forEach((result, index) => {
      if (result.success) {
        setTimeout(() => {
          const originalName = result.originalFile.name.split('.')[0];
          downloadFile(result.compressedBlob, `${originalName}-compressed.${extension}`);
        }, index * 200);
      }
    });

    if (window.showToast) {
      window.showToast(t('downloadStarted'), 'success');
    }
  };

  const handleReset = () => {
    setBatchMode(false);
    setBatchFiles([]);
    setBatchResults([]);
    setCurrentProcessingIndex(0);
    dispatch({ type: 'RESET_TOOL' });
  };

  const compressionStats = state.showResults && state.compressedImageUrl
    ? calculateCompressionStats(state.originalSize, state.processedSize)
    : null;

  // 🆕 批量結果顯示
  if (batchMode && state.showResults && batchResults.length > 0) {
    const successResults = batchResults.filter(r => r.success);
    const totalOriginalSize = successResults.reduce((sum, r) => sum + r.originalFile.size, 0);
    const totalCompressedSize = successResults.reduce((sum, r) => sum + r.compressedBlob.size, 0);
    const totalSaved = totalOriginalSize - totalCompressedSize;
    const totalRatio = Math.round((totalSaved / totalOriginalSize) * 100);

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <ToolHeader title={t('compressionResults')} />

        <div className="p-6">
          {/* 總體統計 */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6 mb-6 border border-green-200">
            <div className="flex items-center gap-2 mb-4">
              <SafeIcon icon={FiInfo} className="text-green-600" />
              <h4 className="font-semibold text-green-800">{t('batchCompressionComplete')}</h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white/60 rounded-lg p-4">
                <span className="text-sm text-gray-600 block mb-1">{t('successfullyProcessed')}</span>
                <p className="font-semibold text-lg text-green-600">
                  {successResults.length}/{batchResults.length} {t('files')}
                </p>
              </div>

              <div className="bg-white/60 rounded-lg p-4">
                <span className="text-sm text-gray-600 block mb-1">{t('totalCompressionRatio')}</span>
                <p className="font-semibold text-lg text-green-600">
                  {totalRatio}%
                </p>
              </div>

              <div className="bg-white/60 rounded-lg p-4">
                <span className="text-sm text-gray-600 block mb-1">{t('totalSaved')}</span>
                <p className="font-semibold text-lg text-green-600">
                  {formatFileSize(totalSaved)}
                </p>
              </div>

              <div className="bg-white/60 rounded-lg p-4">
                <span className="text-sm text-gray-600 block mb-1">{t('sizeChange')}</span>
                <p className="font-semibold text-lg text-gray-800">
                  {formatFileSize(totalOriginalSize)} → {formatFileSize(totalCompressedSize)}
                </p>
              </div>
            </div>
          </div>

          {/* 文件列表 */}
          <div className="space-y-3 mb-6">
            {batchResults.map((result, index) => (
              <motion.div
                key={index}
                className={`p-4 rounded-lg border ${
                  result.success ? 'bg-white border-gray-200' : 'bg-red-50 border-red-200'
                }`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">
                      {result.originalFile.name}
                    </p>
                    {result.success ? (
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="text-gray-600">
                          {formatFileSize(result.originalFile.size)} → {formatFileSize(result.compressedBlob.size)}
                        </span>
                        <span className="text-green-600 font-medium">
                          {t('spaceSaved')} {result.stats.ratio}
                        </span>
                      </div>
                    ) : (
                      <p className="text-sm text-red-600 mt-1">
                        ❌ {result.error}
                      </p>
                    )}
                  </div>

                  {result.success && (
                    <button
                      onClick={() => {
                        const extension = state.imageFormat === 'image/jpeg' ? 'jpg' : state.imageFormat === 'image/png' ? 'png' : 'webp';
                        const originalName = result.originalFile.name.split('.')[0];
                        downloadFile(result.compressedBlob, `${originalName}-compressed.${extension}`);
                      }}
                      className="ml-4 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
                    >
                      {t('download')}
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* 操作按鈕 */}
          <div className="flex gap-3 justify-center">
            <motion.button
              className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center gap-2 shadow-md"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleBatchDownload}
            >
              <SafeIcon icon={FiDownload} />
              {t('downloadAll')} ({successResults.length} {t('files')})
            </motion.button>

            <motion.button
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleReset}
            >
              {t('startOver')}
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  // 單個文件結果顯示(帶對比滑塊)
  if (state.showResults && state.compressedImageUrl && !batchMode) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <ToolHeader title={t('compressionResults')} />

        <div className="p-6">
          {/* 🆕 圖片對比滑塊 */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-600 mb-3">
              {t('comparisonPreview')}
            </h4>
            <ImageComparisonSlider
              beforeImage={state.imagePreview}
              afterImage={state.compressedImageUrl}
              beforeLabel={t('original')}
              afterLabel={t('compressed')}
            />
          </div>

          {compressionStats && compressionStats.isReduced && (
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6 mb-6 border border-green-200">
              <div className="flex items-center gap-2 mb-4">
                <SafeIcon icon={FiInfo} className="text-green-600" />
                <h4 className="font-semibold text-green-800">{t('compressionSuccessful')}</h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/60 rounded-lg p-4">
                  <span className="text-sm text-gray-600 block mb-1">{t('compressionRatio')}</span>
                  <p className="font-semibold text-lg text-green-600">
                    {compressionStats.ratio}
                  </p>
                </div>

                <div className="bg-white/60 rounded-lg p-4">
                  <span className="text-sm text-gray-600 block mb-1">{t('spaceSaved')}</span>
                  <p className="font-semibold text-lg text-green-600">
                    {formatFileSize(compressionStats.savedSpace)}
                  </p>
                </div>

                <div className="bg-white/60 rounded-lg p-4">
                  <span className="text-sm text-gray-600 block mb-1">{t('beforeAfter')}</span>
                  <p className="font-semibold text-lg text-gray-800">
                    {formatFileSize(compressionStats.originalSize)} → {formatFileSize(compressionStats.compressedSize)}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 justify-center">
            <motion.button
              className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center gap-2 shadow-md"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleDownload}
            >
              <SafeIcon icon={FiDownload} />
              {t('downloadImage')}
            </motion.button>

            <motion.button
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleReset}
            >
              {t('startOver')}
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <ToolHeader
        title={t('imageCompress')}
        filename={state.selectedFile?.name}
      />

      <div className="p-6">
        <p className="text-gray-600 mb-6">
          {t('imageCompressDesc')}
        </p>

        {!state.showSettings ? (
          <div className="space-y-4">
            {/* 🆕 模式切換 */}
            <div className="flex gap-3 mb-4">
              <motion.button
                className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                  !batchMode
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                onClick={() => setBatchMode(false)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiInfo} />
                {t('singleFile')}
              </motion.button>

              <motion.button
                className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                  batchMode
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                onClick={() => setBatchMode(true)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiLayers} />
                {t('batchProcess')}
              </motion.button>
            </div>

            {batchMode ? (
              <BatchFileUpload
                onFilesUpload={handleBatchFilesUpload}
                acceptedTypes="image/*"
                uploadText={t('dropOrClickImage')}
                supportText={t('supportImageFormats')}
                fileType="image"
                maxFiles={10}
              />
            ) : (
              <FileUpload
                onFileUpload={handleFileUpload}
                acceptedTypes="image/*"
                uploadText={t('dropOrClickImage')}
                supportText={t('supportImageFormats')}
                fileType="image"
              />
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {state.imagePreview && !batchMode && (
              <div className="text-center">
                <img
                  src={state.imagePreview}
                  alt="Preview"
                  className="max-w-full max-h-64 mx-auto rounded-lg border"
                />
                <p className="text-sm text-gray-600 mt-2">
                  {formatFileSize(state.originalSize)}
                </p>
              </div>
            )}

            {batchMode && batchFiles.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-2">
                  {t('filesSelected')} {batchFiles.length} {t('files')}
                </h4>
                <p className="text-sm text-gray-600">
                  {t('totalSize')}: {formatFileSize(batchFiles.reduce((sum, file) => sum + file.size, 0))}
                </p>
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-800 mb-4">
                {t('compressionSettings')}
              </h4>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('outputFormat')}
                  </label>
                  <select
                    value={state.imageFormat}
                    onChange={(e) =>
                      dispatch({
                        type: 'UPDATE_SETTINGS',
                        payload: { imageFormat: e.target.value }
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="image/jpeg">JPG</option>
                    <option value="image/png">PNG</option>
                    <option value="image/webp">{t('webpRecommended')}</option>
                  </select>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex gap-2">
                    <SafeIcon icon={FiInfo} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">{t('smartCompressionMode')}</p>
                      <p className="text-xs">
                        {t('smartCompressionDesc')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <AnimatePresence>
              {state.isLoading && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-purple-50 rounded-lg p-4"
                >
                  <ProgressBar
                    progress={progress}
                    message={progressMessage}
                    showPercentage={true}
                    remainingTime={remainingTime}
                    processingSpeed={processingSpeed}
                    showStats={true}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex gap-3 justify-center">
              <motion.button
                className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-md"
                whileHover={{ scale: state.isLoading ? 1 : 1.02 }}
                whileTap={{ scale: state.isLoading ? 1 : 0.98 }}
                onClick={handleCompress}
                disabled={state.isLoading || (batchMode && batchFiles.length === 0) || (!batchMode && !state.selectedFile)}
              >
                {state.isLoading ? (
                  <>
                    <SafeIcon icon={FiLoader} className="animate-spin" />
                    {batchMode ? t('compressingPleaseWait') : t('compressingPleaseWait')}
                  </>
                ) : (
                  <>
                    {batchMode ? `${t('compressImage')} ${batchFiles.length} ${t('files')}` : t('compressImage')}
                  </>
                )}
              </motion.button>

              <motion.button
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleReset}
              >
                {t('reuploadImage')}
              </motion.button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ImageCompressTool;